/**
 * 🎓 GuidedSessionPlayer — the user's custom avatar mimics each exercise
 * or cooking step so they learn form + technique independently.
 *
 * Design intent (Feb 2026, owner-locked):
 *   - AR (real camera) is for the OUTSIDE world — anchors, real gear
 *     inventory, hydration reminders.
 *   - Teaching happens HERE: a big, prominent avatar with a clear
 *     demonstration pose per step + voice narration + next/prev.
 *   - Difficulty tier (newbie / intermediate / advanced) is baked in by
 *     the build-session call; we just render whatever the session says.
 *
 * Inputs
 *   session:     result of POST /api/equipment/build-session (either a
 *                workout with {blocks[].moves[]} or a recipe with
 *                {ingredients[], steps[]}).
 *   difficulty:  "newbie" | "intermediate" | "advanced"
 *   space:       "gym" | "kitchen"  (drives pose vocabulary)
 *   onClose:     fired when the user exits the player.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AvatarPreview } from "./AvatarPreview";
import { ChevronLeft, ChevronRight, Volume2, VolumeX, X, Play, Pause, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Pose vocabulary — SVG transform + glow color per action.
const POSES = {
  idle:        { transform: "rotate(0deg)",              shadow: "drop-shadow(0 0 10px rgba(255,255,255,0.2))", label: "ready" },
  demo_push:   { transform: "rotate(-6deg) scale(1.04)", shadow: "drop-shadow(0 0 14px #10b981)",              label: "pushing" },
  demo_pull:   { transform: "rotate(6deg) scale(1.04)",  shadow: "drop-shadow(0 0 14px #10b981)",              label: "pulling" },
  demo_squat:  { transform: "translateY(6px) scale(0.94)",shadow: "drop-shadow(0 0 14px #10b981)",             label: "squatting" },
  demo_hinge:  { transform: "rotate(-14deg) translateY(4px)", shadow: "drop-shadow(0 0 14px #10b981)",         label: "hinging" },
  demo_press:  { transform: "translateY(-4px) scale(1.02)",shadow: "drop-shadow(0 0 14px #10b981)",            label: "pressing up" },
  demo_core:   { transform: "rotate(-4deg) translateY(2px)",shadow: "drop-shadow(0 0 14px #10b981)",           label: "core work" },
  cook_stir:   { transform: "rotate(-8deg)",             shadow: "drop-shadow(0 0 14px #fb923c)",              label: "stirring" },
  cook_chop:   { transform: "rotate(4deg) translateY(4px)",shadow: "drop-shadow(0 0 14px #fb923c)",            label: "chopping" },
  cook_pour:   { transform: "rotate(12deg)",             shadow: "drop-shadow(0 0 14px #fb923c)",              label: "pouring" },
  cook_watch:  { transform: "rotate(0deg) scale(1.02)",  shadow: "drop-shadow(0 0 14px #fb923c)",              label: "watching pan" },
  hydrate:     { transform: "rotate(0deg)",              shadow: "drop-shadow(0 0 14px #38bdf8)",              label: "hydrate" },
  cheer:       { transform: "rotate(-8deg) scale(1.10)", shadow: "drop-shadow(0 0 18px #f59e0b)",              label: "done" },
};

// Simple heuristic: map a move / step name to a pose id.
function pickPose(kind, text) {
  const t = String(text || "").toLowerCase();
  if (/water|hydrat|sip|drink|break/.test(t)) return "hydrate";
  if (kind === "gym") {
    if (/push|press|bench/.test(t)) return /overhead|shoulder/.test(t) ? "demo_press" : "demo_push";
    if (/pull|row|curl|lat/.test(t)) return "demo_pull";
    if (/squat|lunge|step-up/.test(t)) return "demo_squat";
    if (/deadlift|hinge|rdl|swing/.test(t)) return "demo_hinge";
    if (/plank|crunch|core|abs|situp|sit-up/.test(t)) return "demo_core";
    return "demo_push";
  }
  // kitchen
  if (/stir|whisk|mix|fold/.test(t)) return "cook_stir";
  if (/chop|slice|dice|mince|cut/.test(t)) return "cook_chop";
  if (/pour|add|drizzle/.test(t)) return "cook_pour";
  if (/watch|simmer|bake|reduce|wait|rest/.test(t)) return "cook_watch";
  return "cook_stir";
}

// Flatten a built session into a list of teachable steps
function flattenSession(session, space) {
  if (!session) return [];
  const steps = [];

  // 1. Intro
  steps.push({
    id: "intro",
    title: session.title || "Session",
    text:  session.coach_note || "",
    pose:  "idle",
    kind:  "intro",
  });

  // Safety note (pet safety, hot surfaces, etc.)
  if (session.safety_note) {
    steps.push({
      id: "safety",
      title: "Note",
      text:  session.safety_note,
      pose:  "idle",
      kind:  "safety",
    });
  }

  // 2. Workout structure
  if (Array.isArray(session.blocks) && session.blocks.length) {
    session.blocks.forEach((b, bi) => {
      steps.push({
        id: `block_${bi}`,
        title: b.name || `Block ${bi + 1}`,
        text: b.name || "",
        pose: "idle",
        kind: "block",
      });
      (b.moves || []).forEach((m, mi) => {
        const isBreak = !!m.is_break || /break|rest|water|hydrat/i.test(m.name || "");
        steps.push({
          id: `m_${bi}_${mi}`,
          title: m.name || "Move",
          text: `${m.sets || 1} sets × ${m.reps || "steady reps"}. ${m.cues || ""}`.trim(),
          pose: isBreak ? "hydrate" : pickPose(space, m.name),
          kind: isBreak ? "break" : "move",
          detail: {
            sets: m.sets,
            reps: m.reps,
            cues: m.cues,
            equipment: m.equipment_used || [],
          },
        });
      });
    });
  }

  // 3. Recipe structure
  if (Array.isArray(session.ingredients) && session.ingredients.length) {
    steps.push({
      id: "ingredients",
      title: "Ingredients",
      text: session.ingredients.map((i) => `${i.qty || ""} ${i.item || ""}`.trim()).join(" · "),
      pose: "idle",
      kind: "ingredients",
    });
  }
  if (Array.isArray(session.steps) && session.steps.length) {
    session.steps.forEach((s, i) => {
      steps.push({
        id: `s_${i}`,
        title: `Step ${s.step ?? i + 1}`,
        text: s.text || "",
        pose: pickPose("kitchen", s.text),
        kind: "step",
        detail: {
          equipment: s.equipment_used || [],
        },
      });
    });
  }

  // 4. Outro
  steps.push({
    id: "outro",
    title: "Done",
    text: session.coach_note || "Session finished.",
    pose: "cheer",
    kind: "outro",
  });

  return steps;
}

export function GuidedSessionPlayer({ session, difficulty = "intermediate", space = "gym", onClose }) {
  const [avatar, setAvatar] = useState(null);
  const [current, setCurrent] = useState(0);
  const [voiceOn, setVoiceOn] = useState(true);
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [chunks, setChunks] = useState([]);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  // 🏠 Virtual-room backdrop — the user's own room photos serve as the stage
  //    so the avatar appears to perform IN their actual gym / kitchen.
  const [roomShots, setRoomShots] = useState([]);
  const [roomIdx, setRoomIdx] = useState(0);
  const [showRoomPicker, setShowRoomPicker] = useState(false);
  const audioRef = useRef(null);
  const advanceTimerRef = useRef(null);

  const steps = useMemo(() => flattenSession(session, space), [session, space]);
  const step = steps[current];
  const total = steps.length;

  // Fetch the user's avatar once
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/avatar`);
        setAvatar(r.data || null);
      } catch { setAvatar(null); }
    })();
  }, []);

  // Fetch the user's room gallery for this space (used as backdrop)
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/room/gallery`, { params: { space, limit: 30 } });
        setRoomShots(r.data?.snapshots || []);
      } catch { setRoomShots([]); }
    })();
  }, [space]);

  // Cycle the room backdrop every 8s if there are multiple shots — makes
  // the "virtual room" feel alive without the user having to fiddle.
  useEffect(() => {
    if (roomShots.length <= 1) return;
    const t = setInterval(() => setRoomIdx((i) => (i + 1) % roomShots.length), 8000);
    return () => clearInterval(t);
  }, [roomShots.length]);

  // Voice: on-demand fetch of TTS chunks for the whole session
  const loadVoice = useCallback(async () => {
    if (!session || chunks.length > 0) return;
    setVoiceLoading(true);
    try {
      const r = await axios.post(`${API}/equipment/voice-session`, { session, difficulty, space });
      setChunks(r.data?.chunks || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice coach unavailable.");
    } finally {
      setVoiceLoading(false);
    }
  }, [session, difficulty, chunks.length, space]);

  useEffect(() => { if (voiceOn) loadVoice(); }, [voiceOn, loadVoice]);

  // Play audio for current step whenever it changes (and voice is on)
  useEffect(() => {
    if (!voiceOn || !audioRef.current || chunks.length === 0) return;
    // Map step index → chunk index; the TTS script has slightly different
    // structure (intro, safety, block, move…), but ordering is preserved.
    const chunk = chunks[current];
    if (!chunk || !chunk.audio_base64) return;
    const src = `data:${chunk.mime_type || "audio/mpeg"};base64,${chunk.audio_base64}`;
    audioRef.current.src = src;
    audioRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
  }, [current, chunks, voiceOn]);

  // Auto-advance timer
  useEffect(() => {
    if (advanceTimerRef.current) { clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
    if (!autoAdvance) return;
    const delay = (step?.kind === "move" || step?.kind === "step") ? 8000 : 4000;
    advanceTimerRef.current = setTimeout(() => {
      setCurrent((c) => Math.min(total - 1, c + 1));
    }, delay);
    return () => { if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current); };
  }, [current, autoAdvance, step, total]);

  const goPrev = () => setCurrent((c) => Math.max(0, c - 1));
  const goNext = () => setCurrent((c) => Math.min(total - 1, c + 1));

  const pose = POSES[step?.pose] || POSES.idle;
  const kbStyle = step?.pose === "cheer" ? { animation: "gspCheer 900ms ease-in-out infinite" } : { animation: "gspBob 3.5s ease-in-out infinite" };

  // 🎨 Space personality — differentiated atmosphere (owner Feb 2026 directive:
  //   "the kitchen and gym sound way to the same. They should not.")
  const isKitchen = space === "kitchen";
  const tint = isKitchen ? "from-amber-900/70 via-orange-950/60 to-black" : "from-slate-800/70 via-slate-950/60 to-black";
  const accent = isKitchen ? "amber" : "slate";
  const accentText = isKitchen ? "text-amber-300" : "text-slate-300";
  const accentBorder = isKitchen ? "border-amber-400/40" : "border-slate-400/40";
  const primaryBtn = isKitchen ? "bg-amber-500 hover:bg-amber-400" : "bg-slate-300 hover:bg-white";
  const vocabLabel = isKitchen ? "kitchen · mise → plate" : "training floor · load → rest";

  // Current room backdrop (falls back to gradient when none)
  const activeRoom = roomShots[roomIdx];

  // The API returns { user_id, physical, accessibility, style, exists, ... }
  // with the builder fields at the TOP level, not wrapped in a `params` key.
  // AvatarPreview reads params.physical/.accessibility/.style so we pass the
  // whole avatar record straight through.
  const hasAvatarParams = !!(avatar && (
    avatar.physical || avatar.accessibility || avatar.style
  ) && (avatar.exists !== false));
  const phys = avatar?.physical || {};
  const weightLine = phys.weight_kg && phys.height_cm
    ? `${phys.height_cm} cm · ${phys.weight_kg} kg`
    : null;
  const depthLabel = {
    just_show: "▶ Just moves",
    explain_why: "◐ With context",
    pro_mode: "◉ Pro details",
    // legacy fallbacks
    newbie: "▶ Just moves",
    intermediate: "◐ With context",
    advanced: "◉ Pro details",
  }[difficulty] || "◐ With context";

  if (!session || total === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex flex-col"
      data-testid="guided-session-player"
      style={{
        // Room snapshot serves as the actual backdrop so the avatar
        // appears to be in the user's real gym / kitchen.
        backgroundImage: activeRoom?.photo_thumb_b64
          ? `linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.85)), url("data:image/jpeg;base64,${activeRoom.photo_thumb_b64}")`
          : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Fallback gradient when no room shots yet */}
      {!activeRoom && (
        <div className={`absolute inset-0 pointer-events-none bg-gradient-to-b ${tint}`} />
      )}
      {/* Space-tinted overlay always on top of the photo for readability */}
      {activeRoom && (
        <div className={`absolute inset-0 pointer-events-none bg-gradient-to-b ${tint} mix-blend-multiply opacity-70`} />
      )}
      {/* Keyframes */}
      <style>{`
        @keyframes gspBob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        @keyframes gspCheer { 0%,100% { transform: translateY(0) scale(1.10); } 50% { transform: translateY(-10px) scale(1.16); } }
        @keyframes gspFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Top bar */}
      <div className="relative flex-shrink-0 flex items-center justify-between gap-2 p-3 border-b border-white/10 bg-black/40 backdrop-blur">
        <div className="min-w-0">
          <div className={`text-[10px] uppercase tracking-widest ${accentText}`}>
            {depthLabel} · {vocabLabel} · step {current + 1} / {total}
          </div>
          <div className="text-sm font-bold text-white truncate">{session.title || "Your session"}</div>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Room picker — cycle backdrops if user has multiple snaps */}
          {roomShots.length > 1 && (
            <button
              type="button"
              onClick={() => setRoomIdx((i) => (i + 1) % roomShots.length)}
              className={`p-2 rounded-full border ${accentBorder} bg-black/40 hover:bg-white/10`}
              data-testid="gsp-room-cycle"
              aria-label="Switch backdrop"
              title={`Backdrop ${roomIdx + 1} / ${roomShots.length}`}
            >
              <span className="text-[10px] font-bold text-white">🖼️</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setVoiceOn((v) => !v)}
            className={`p-2 rounded-full border ${voiceOn ? `bg-${accent}-600/30 ${accentBorder}` : "bg-black/40 border-white/20"}`}
            data-testid="gsp-voice-toggle"
            aria-label="Toggle voice"
          >
            {voiceLoading ? <Loader2 size={14} className="animate-spin text-white" /> : voiceOn ? <Volume2 size={14} className={accentText} /> : <VolumeX size={14} className="text-white/70" />}
          </button>
          <button
            type="button"
            onClick={() => setAutoAdvance((v) => !v)}
            className={`p-2 rounded-full border ${autoAdvance ? "bg-amber-500/30 border-amber-400" : "bg-black/40 border-white/20"}`}
            data-testid="gsp-autoadvance-toggle"
            aria-label="Toggle auto-advance"
          >
            {autoAdvance ? <Pause size={14} className="text-amber-200" /> : <Play size={14} className="text-white/80" />}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full bg-black/40 border border-white/20 hover:bg-red-500/30"
            data-testid="gsp-close"
            aria-label="Exit"
          >
            <X size={14} className="text-white" />
          </button>
        </div>
      </div>

      {/* Avatar stage — clean, no ambient rings, room backdrop does the atmosphere */}
      <div className="relative flex-1 flex flex-col items-center justify-center px-4 py-2 overflow-hidden">

        <div
          className="relative"
          style={{ filter: pose.shadow, ...kbStyle }}
          data-testid="gsp-avatar-stage"
        >
          <div
            className="relative"
            style={{ transform: pose.transform, transition: "transform 400ms ease" }}
            data-current-pose={step?.pose}
          >
            {hasAvatarParams ? (
              <AvatarPreview params={avatar} size={300} />
            ) : (
              <div className="w-64 h-64 rounded-full bg-black/40 border-2 border-emerald-400/40 flex items-center justify-center text-6xl">
                <a href="/avatar" className="hover:opacity-80 transition text-center block px-4" data-testid="gsp-avatar-empty">
                  <div className="text-4xl">🧍</div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mt-2">Build your avatar</div>
                </a>
              </div>
            )}
          </div>
        </div>

        <div className="absolute bottom-4 left-4 right-4 max-w-md mx-auto text-center pointer-events-none">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300">{pose.label}</div>
          {hasAvatarParams && weightLine && (
            <div className="text-[9px] uppercase tracking-widest text-white/40 mt-0.5" data-testid="gsp-avatar-stats">
              {weightLine}
              {phys.body_build && <span> · {phys.body_build}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Step card */}
      <div className="relative flex-shrink-0 p-3 border-t border-white/10 bg-black/70 backdrop-blur">
        <div key={step?.id} style={{ animation: "gspFadeIn 350ms ease-out" }}>
          <div className="text-sm font-black text-white uppercase tracking-wider">
            {step?.title}
          </div>
          <div className="text-sm text-white/85 mt-1">{step?.text}</div>
          {step?.detail?.cues && (
            <div className="text-[11px] italic text-emerald-200 mt-1 border-l-2 border-emerald-400/50 pl-2">
              Cue: {step.detail.cues}
            </div>
          )}
          {Array.isArray(step?.detail?.equipment) && step.detail.equipment.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {step.detail.equipment.map((e, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-white/70">
                  {e}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between gap-2 mt-3">
          <button
            type="button"
            onClick={goPrev}
            disabled={current === 0}
            className="flex-1 inline-flex items-center justify-center gap-1 py-2 rounded-lg bg-black/60 border border-white/20 text-white text-xs font-bold hover:bg-white/10 disabled:opacity-40"
            data-testid="gsp-prev"
          >
            <ChevronLeft size={14} /> Prev
          </button>
          <div className="text-[10px] font-mono text-white/60">
            {current + 1} / {total}
          </div>
          <button
            type="button"
            onClick={goNext}
            disabled={current === total - 1}
            className="flex-1 inline-flex items-center justify-center gap-1 py-2 rounded-lg bg-emerald-500 text-black text-xs font-black uppercase hover:bg-emerald-400 disabled:opacity-40"
            data-testid="gsp-next"
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
        <div className="text-center mt-1 text-[10px] text-white/40 flex items-center justify-center gap-1">
          <Sparkles size={10} /> {depthLabel} · avatar in your {space === "kitchen" ? "kitchen" : "space"}
        </div>
      </div>

      {/* Voice audio player (hidden) */}
      <audio
        ref={audioRef}
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
        preload="auto"
        className="hidden"
        data-testid="gsp-audio"
      />
    </div>
  );
}

export default GuidedSessionPlayer;
